import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Search, Package, Tag, ListChecks } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLOR = {
  Fast: 'bg-emerald-600',
  Medium: 'bg-amber-500',
  Slow: 'bg-slate-500',
  'High Value': 'bg-rose-600',
  '': 'bg-slate-400',
};

const fmtR = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

/** Sales Team Price Guide — read-only, searchable view of the current
 *  Auto-Spares master list with live stock quantity next to each item. */
export default function SalesPriceGuide() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSalesman = currentUser?.role === 'auto_spares_salesman';
  const [items, setItems] = useState([]);
  const [lastSold, setLastSold] = useState({}); // product_id -> { price, date }
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('__all__');
  const [prio, setPrio] = useState('__all__');

  useEffect(() => {
    (async () => {
      try {
        const [prodRes, histRes] = await Promise.all([
          axios.get(`${API_URL}/api/auto-spares/products`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/api/auto-spares/products/future-value`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: { breakdown: [] } })),
        ]);
        setItems(Array.isArray(prodRes.data) ? prodRes.data : []);
        const bd = (histRes.data?.breakdown || []).filter(r => r.source === 'history');
        const map = {};
        for (const r of bd) {
          if (r.product_id) map[r.product_id] = {
            price: Number(r.last_sold_price) || 0,
            date: r.last_sale_date || null,
            sale_count: r.sale_count || 0,
          };
        }
        setLastSold(map);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load price list');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const categories = useMemo(
    () => Array.from(new Set(items.map((x) => x.category).filter(Boolean))).sort(),
    [items],
  );
  const priorities = useMemo(
    () => Array.from(new Set(items.map((x) => x.priority).filter(Boolean))).sort(),
    [items],
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const hasFilter = query.length > 0 || cat !== '__all__' || prio !== '__all__';
    // Salesmen must type/filter to see any rows — prevents casual browsing
    // of the entire catalog and stock totals.
    if (isSalesman && !hasFilter) return [];
    return items.filter((p) => {
      if (cat !== '__all__' && p.category !== cat) return false;
      if (prio !== '__all__' && (p.priority || '') !== prio) return false;
      if (!query) return true;
      const code = (p.supplier_sku || p.master_code || p.sku || '').toLowerCase();
      return code.includes(query)
        || (p.name || '').toLowerCase().includes(query)
        || (p.category || '').toLowerCase().includes(query);
    }).sort((a, b) => {
      const ca = (a.supplier_sku || a.master_code || a.sku || '').localeCompare(
        b.supplier_sku || b.master_code || b.sku || '',
      );
      return ca;
    });
  }, [items, q, cat, prio, isSalesman]);

  const summary = useMemo(() => {
    const total = filtered.length;
    const totalValue = filtered.reduce((s, p) => s + (Number(p.selling_price ?? p.sale_price) || 0) * (Number(p.quantity) || 0), 0);
    const inStock = filtered.filter((p) => (Number(p.quantity) || 0) > 0).length;
    return { total, totalValue, inStock };
  }, [filtered]);

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Tag className="h-6 w-6 text-amber-400" /> Sales Team Price Guide
            </h1>
            <p className="text-sm text-slate-400">
              {isSalesman
                ? 'Type a part name or code below to look up its price.'
                : `JSM Auto Spares master selling-price list — ${items.length} items · imported Feb 2026`}
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="price-guide-back">← Back</Button>
        </div>

        {/* Summary — hidden for salesmen so they can't see aggregate stock value */}
        {!isSalesman && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
          <Card className="bg-gradient-to-br from-amber-600 to-amber-700 border-0 text-white">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase opacity-80 flex items-center gap-1"><Package className="h-3 w-3" /> Items shown</div>
              <div className="text-2xl font-bold" data-testid="pg-count">{summary.total}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0 text-white">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase opacity-80 flex items-center gap-1"><ListChecks className="h-3 w-3" /> In stock</div>
              <div className="text-2xl font-bold" data-testid="pg-instock">{summary.inStock}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-700 to-slate-900 border-0 text-white">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase opacity-80">Stock value @ selling price</div>
              <div className="text-2xl font-bold" data-testid="pg-value">{fmtR(summary.totalValue)}</div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Filters */}
        <Card className="bg-slate-900 border-slate-700 mb-4">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="h-4 w-4 absolute top-2.5 left-2 text-slate-500" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by code, name, or category…"
                className="pl-8 bg-slate-800 border-slate-600 text-white"
                data-testid="pg-search"
              />
            </div>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="w-44 bg-slate-800 border-slate-600" data-testid="pg-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All categories</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={prio} onValueChange={setPrio}>
              <SelectTrigger className="w-36 bg-slate-800 border-slate-600" data-testid="pg-priority">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All priorities</SelectItem>
                {priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm text-slate-200">Master Price List</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table data-testid="pg-table">
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300">Code</TableHead>
                  <TableHead className="text-slate-300">Item</TableHead>
                  <TableHead className="text-slate-300">Category</TableHead>
                  <TableHead className="text-center text-slate-300">Priority</TableHead>
                  <TableHead className="text-right text-slate-300">Target</TableHead>
                  <TableHead className="text-right text-slate-300">On Hand</TableHead>
                  <TableHead className="text-right text-slate-300">Selling Price</TableHead>
                  <TableHead className="text-right text-slate-300">Last Sold</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-6">Loading…</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-400 py-10 italic" data-testid="pg-empty-state">
                      {isSalesman && q.trim() === '' && cat === '__all__' && prio === '__all__'
                        ? '🔍  Type a part name or Stock Code above to look up its price.'
                        : 'No items match your filter.'}
                    </TableCell>
                  </TableRow>
                ) : filtered.map((p) => {
                  const code = p.supplier_sku || p.master_code || p.sku || '';
                  const qty = Number(p.quantity) || 0;
                  const target = Number(p.target_stock_level) || 0;
                  const price = Number(p.selling_price ?? p.sale_price) || 0;
                  const low = target > 0 && qty < target;
                  const ls = lastSold[p.id];
                  return (
                    <TableRow key={p.id} className="border-slate-800 hover:bg-slate-800/50" data-testid={`pg-row-${code}`}>
                      <TableCell className="font-mono text-amber-300 text-xs font-bold">{code}</TableCell>
                      <TableCell className="text-white">{p.name}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{p.category || '—'}</TableCell>
                      <TableCell className="text-center">
                        {p.priority
                          ? <Badge className={`${PRIORITY_COLOR[p.priority] || 'bg-slate-500'} text-white text-[10px]`}>{p.priority}</Badge>
                          : <span className="text-slate-500 text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-400 text-xs">{target || '—'}</TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${qty === 0 ? 'text-rose-400' : low ? 'text-amber-300' : 'text-emerald-400'}`}>
                        {qty}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold text-emerald-400">{fmtR(price)}</TableCell>
                      <TableCell className="text-right font-mono text-xs" data-testid={`pg-lastsold-${code}`}>
                        {ls ? (
                          <div>
                            <div className="text-cyan-300 font-semibold">{fmtR(ls.price)}</div>
                            <div className="text-slate-500 text-[10px]">
                              {ls.date ? new Date(ls.date).toISOString().slice(0, 10) : ''}
                              {ls.sale_count > 1 ? ` · ${ls.sale_count} sales` : ''}
                            </div>
                          </div>
                        ) : <span className="text-slate-600">—</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
